import React from 'react';
import Icon from '../../../components/AppIcon';

const SimilarityScoreDisplay = ({ 
  overallScore = 0,
  matchingCriteria = [],
  className = '' 
}) => {
  const getScoreColor = (score) => {
    if (score >= 0.95) return 'text-success';
    if (score >= 0.85) return 'text-warning';
    return 'text-error';
  };

  const getScoreBackground = (score) => {
    if (score >= 0.95) return 'bg-success bg-opacity-10 border-success border-opacity-20';
    if (score >= 0.85) return 'bg-warning bg-opacity-10 border-warning border-opacity-20';
    return 'bg-error bg-opacity-10 border-error border-opacity-20';
  };

  const getCriteriaIcon = (type) => {
    switch (type) {
      case 'ean': return 'Hash';
      case 'mpn': return 'Tag';
      case 'brand': return 'Building2';
      case 'name': return 'Type';
      case 'description': return 'FileText';
      default: return 'Check';
    }
  };

  const getCriteriaColor = (score) => {
    if (score >= 0.9) return 'text-success';
    if (score >= 0.7) return 'text-warning';
    return 'text-muted-foreground';
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      {/* Overall Similarity Score */}
      <div className={`text-center p-4 rounded-lg border ${getScoreBackground(overallScore)}`}>
        <div className="flex items-center justify-center space-x-2 mb-2">
          <Icon 
            name="Target" 
            size={20} 
            className={getScoreColor(overallScore)}
          />
          <span className="text-sm font-medium text-muted-foreground">
            Overall Similarity
          </span>
        </div>
        <div className={`text-3xl font-bold ${getScoreColor(overallScore)}`}>
          {Math.round(overallScore * 100)}%
        </div>
        <div className="mt-2 w-full bg-muted rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              overallScore >= 0.95 ? 'bg-success' : 
              overallScore >= 0.85 ? 'bg-warning' : 'bg-error'
            }`}
            style={{ width: `${overallScore * 100}%` }}
          />
        </div>
      </div>
      {/* Matching Criteria Breakdown */}
      <div className="mt-6">
        <h4 className="text-sm font-medium text-foreground mb-4">
          Matching Criteria
        </h4>
        <div className="space-y-3">
          {matchingCriteria?.map((criteria, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon 
                  name={getCriteriaIcon(criteria?.type)} 
                  size={16} 
                  className={getCriteriaColor(criteria?.score)}
                />
                <span className="text-sm text-foreground capitalize">
                  {criteria?.type}
                </span>
                {criteria?.isExactMatch && (
                  <span className="px-2 py-0.5 bg-success text-success-foreground text-xs font-medium rounded">
                    Exact
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-sm font-medium ${getCriteriaColor(criteria?.score)}`}>
                  {Math.round(criteria?.score * 100)}%
                </span>
                <div className="w-16 bg-muted rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      criteria?.score >= 0.9 ? 'bg-success' : 
                      criteria?.score >= 0.7 ? 'bg-warning' : 'bg-muted-foreground'
                    }`}
                    style={{ width: `${criteria?.score * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Confidence Level */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">
            Confidence Level
          </span>
          <span className={`text-sm font-semibold ${getScoreColor(overallScore)}`}>
            {overallScore >= 0.95 ? 'High' : 
             overallScore >= 0.85 ? 'Medium' : 'Low'}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {overallScore >= 0.95 
            ? 'Strong match with multiple exact criteria'
            : overallScore >= 0.85 
            ? 'Good match but requires manual verification'
            : 'Weak match, consider keeping separate'
          }
        </p>
      </div>
    </div>
  );
};

export default SimilarityScoreDisplay;